import React, { createContext, useContext, useState, ReactNode } from 'react';

export type BookingMode = 'new' | 'reschedule' | 'cancel';

export interface ExistingBooking {
  id: string;
  booking_date: string;
  duration: number;
  timezone: string;
  meeting_name: string;
  booker_name: string;
  booker_email: string;
  booker_company?: string;
  message?: string;
  status: string;
  meeting_link_id: string;
  host_user_id: string;
  calendar_event_id?: string;
}

interface BookingContextType {
  mode: BookingMode;
  existingBooking: ExistingBooking | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  setMode: (mode: BookingMode) => void;
  setExistingBooking: (booking: ExistingBooking | null) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedTime: (time: string | null) => void;
  isRescheduleMode: boolean;
  isCancelMode: boolean;
  isNewBookingMode: boolean;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

interface BookingProviderProps {
  children: ReactNode;
  initialMode?: BookingMode;
  initialBooking?: ExistingBooking | null;
}

export const BookingProvider: React.FC<BookingProviderProps> = ({
  children,
  initialMode = 'new',
  initialBooking = null
}) => {
  const [mode, setMode] = useState<BookingMode>(initialMode);
  const [existingBooking, setExistingBooking] = useState<ExistingBooking | null>(initialBooking);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const value: BookingContextType = {
    mode,
    existingBooking,
    selectedDate,
    selectedTime,
    setMode,
    setExistingBooking,
    setSelectedDate,
    setSelectedTime,
    isRescheduleMode: mode === 'reschedule',
    isCancelMode: mode === 'cancel',
    isNewBookingMode: mode === 'new'
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBookingContext = (): BookingContextType => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBookingContext must be used within a BookingProvider');
  }
  return context;
};
